import { useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useT } from '@/i18n/use-t';
import { useLocations } from '@/hooks/use-locations';
import {
  useCreateMasterkasaSource,
  useUpdateMasterkasaSource,
  type MasterkasaSource,
  type MasterkasaSourceSettings,
} from '@/hooks/use-masterkasa';

/**
 * Add a Masterkasa source, or change the settings of one (VL-290).
 *
 * Both were API-only until now: creating a store meant a `curl` with eleven
 * fields, and the two settings the projection needs had no field anywhere. The
 * customer asked for the button; the settings are here with it because they
 * are the same conversation — a store is configured once, by the person who
 * just created it.
 *
 * Deliberately not exposed: `statementTimeoutMs`, `batchSize` and
 * `pollIntervalSeconds`. They have working defaults, the runbook says to leave
 * them alone, and a form that asks eleven questions where eight are "leave
 * this" teaches the operator to skim past the three that matter.
 */

/** Digits and separators → the tax-group list the API takes. */
function parseTaxGroups(raw: string): number[] | null {
  const trimmed = raw.trim();
  if (trimmed === '') return [];
  const parts = trimmed.split(/[\s,]+/).filter((part) => part !== '');
  const parsed: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return null;
    parsed.push(Number.parseInt(part, 10));
  }
  return [...new Set(parsed)];
}

export function MasterkasaSourceDialog({
  mode,
  source,
  onClose,
}: {
  mode: 'create' | 'settings';
  /** The source being edited; ignored when creating. */
  source: MasterkasaSource | null;
  onClose: () => void;
}): JSX.Element {
  const t = useT('admin_ui');
  const create = useCreateMasterkasaSource();
  const update = useUpdateMasterkasaSource();
  const locations = useLocations({ status: ['active'] });

  const [locationId, setLocationId] = useState('');
  const [name, setName] = useState(source?.name ?? '');
  const [host, setHost] = useState(source?.host ?? '');
  const [port, setPort] = useState('5432');
  const [databaseName, setDatabaseName] = useState('psp_server');
  const [username, setUsername] = useState('readuser');
  const [password, setPassword] = useState('');
  const [ssl, setSsl] = useState(false);
  const [shopId, setShopId] = useState(source?.shopId ?? '');
  const [currencyCode, setCurrencyCode] = useState(source?.currencyCode ?? 'UAH');
  const [restrictedAgeYears, setRestrictedAgeYears] = useState(
    String(source?.restrictedAgeYears ?? 18),
  );
  const [taxGroups, setTaxGroups] = useState((source?.ageRestrictedTaxGroups ?? []).join(', '));
  const [ageAssetKey, setAgeAssetKey] = useState(source?.ageAssetKey ?? '');
  // RL-453: export settings, settings mode only. The write password is never
  // read back — the field starts empty and an empty field keeps what is stored.
  const [exportEnabled, setExportEnabled] = useState(source?.exportEnabled ?? false);
  const [exportZReports, setExportZReports] = useState(source?.exportZReports ?? false);
  const [exportUsername, setExportUsername] = useState(source?.exportUsername ?? '');
  const [exportPassword, setExportPassword] = useState('');
  const [cashIn, setCashIn] = useState(String(source?.cashInReceiptType ?? ''));
  const [cashOut, setCashOut] = useState(String(source?.cashOutReceiptType ?? ''));
  const [error, setError] = useState<string | null>(null);

  const creating = mode === 'create';
  const pending = create.isPending || update.isPending;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const groups = parseTaxGroups(taxGroups);
    if (groups === null) {
      setError(t('settings.integrations.masterkasa.form.taxGroupsInvalid'));
      return;
    }
    const age = Number(restrictedAgeYears);
    if (!Number.isSafeInteger(age) || age < 1 || age > 120) {
      setError(t('settings.integrations.masterkasa.form.ageInvalid'));
      return;
    }
    const settings = {
      shopId: shopId.trim() === '' ? null : shopId.trim(),
      currencyCode: currencyCode.trim().toUpperCase(),
      restrictedAgeYears: age,
      ageRestrictedTaxGroups: groups,
      ageAssetKey: ageAssetKey.trim() === '' ? null : ageAssetKey.trim(),
    };
    const exportChanges = creating ? {} : exportSettingsChanges();
    if (exportChanges === null) return;

    try {
      if (creating) {
        const portNumber = Number(port);
        if (!Number.isSafeInteger(portNumber) || portNumber < 1 || portNumber > 65_535) {
          setError(t('settings.integrations.masterkasa.form.portInvalid'));
          return;
        }
        await create.mutateAsync({
          locationId,
          name: name.trim(),
          host: host.trim(),
          port: portNumber,
          databaseName: databaseName.trim(),
          username: username.trim(),
          password,
          ssl,
          ...settings,
        });
      } else if (source !== null) {
        await update.mutateAsync({
          sourceId: source.id,
          settings: { ...settings, ...exportChanges },
        });
      }
      onClose();
    } catch (cause) {
      // The server's own refusal ("export needs the store's shop ID", …) is
      // the useful message; the transport's "status code 400" is not.
      const body = isAxiosError(cause)
        ? (cause.response?.data as { message?: unknown } | undefined)
        : undefined;
      setError(
        typeof body?.message === 'string'
          ? body.message
          : cause instanceof Error
            ? cause.message
            : t('settings.integrations.masterkasa.form.saveError'),
      );
    }
  }

  /**
   * Only the export fields the operator changed, so saving an unrelated
   * setting never re-sends them — and a password only when one was typed.
   * Null (after showing why) when a receipt code is not a number.
   */
  function exportSettingsChanges(): Partial<MasterkasaSourceSettings> | null {
    const changes: Partial<MasterkasaSourceSettings> = {};
    if (exportEnabled !== (source?.exportEnabled ?? false)) changes.exportEnabled = exportEnabled;
    if (exportZReports !== (source?.exportZReports ?? false)) {
      changes.exportZReports = exportZReports;
    }
    const username = exportUsername.trim() === '' ? null : exportUsername.trim();
    if (username !== (source?.exportUsername ?? null)) changes.exportUsername = username;
    if (exportPassword !== '') changes.exportPassword = exportPassword;
    for (const [raw, before, field] of [
      [cashIn, source?.cashInReceiptType, 'cashInReceiptType'],
      [cashOut, source?.cashOutReceiptType, 'cashOutReceiptType'],
    ] as const) {
      if (raw.trim() === '' && before === undefined) continue;
      const code = Number(raw.trim());
      if (!/^\d+$/.test(raw.trim()) || code > 1_000) {
        setError(t('settings.integrations.masterkasa.form.receiptCodeInvalid'));
        return null;
      }
      if (code !== before) changes[field] = code;
    }
    return changes;
  }

  const canSubmit = creating
    ? locationId !== '' && name.trim() !== '' && host.trim() !== '' && password !== ''
    : true;

  return (
    <AlertDialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <AlertDialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {creating
                ? t('settings.integrations.masterkasa.form.createTitle')
                : t('settings.integrations.masterkasa.form.settingsTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {creating
                ? t('settings.integrations.masterkasa.form.createDescription')
                : t('settings.integrations.masterkasa.form.settingsDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {creating && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="mk-location">
                  {t('settings.integrations.masterkasa.form.location')}
                </Label>
                <Select
                  id="mk-location"
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                >
                  <option value="">
                    {t('settings.integrations.masterkasa.form.locationPlaceholder')}
                  </option>
                  {(locations.data?.items ?? []).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.code})
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mk-name">{t('settings.integrations.masterkasa.form.name')}</Label>
                <Input
                  id="mk-name"
                  value={name}
                  maxLength={100}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mk-host">{t('settings.integrations.masterkasa.form.host')}</Label>
                <Input
                  id="mk-host"
                  value={host}
                  maxLength={255}
                  onChange={(event) => setHost(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mk-port">{t('settings.integrations.masterkasa.form.port')}</Label>
                <Input
                  id="mk-port"
                  inputMode="numeric"
                  value={port}
                  onChange={(event) => setPort(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mk-database">
                  {t('settings.integrations.masterkasa.form.database')}
                </Label>
                <Input
                  id="mk-database"
                  value={databaseName}
                  maxLength={128}
                  onChange={(event) => setDatabaseName(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mk-username">
                  {t('settings.integrations.masterkasa.form.username')}
                </Label>
                <Input
                  id="mk-username"
                  value={username}
                  maxLength={128}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mk-password">
                  {t('settings.integrations.masterkasa.form.password')}
                </Label>
                <Input
                  id="mk-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <input
                  id="mk-ssl"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={ssl}
                  onChange={(event) => setSsl(event.target.checked)}
                />
                <Label htmlFor="mk-ssl">{t('settings.integrations.masterkasa.form.ssl')}</Label>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="mk-currency">
                {t('settings.integrations.masterkasa.form.currency')}
              </Label>
              <Input
                id="mk-currency"
                value={currencyCode}
                maxLength={3}
                onChange={(event) => setCurrencyCode(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.currencyHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mk-age">{t('settings.integrations.masterkasa.form.age')}</Label>
              <Input
                id="mk-age"
                inputMode="numeric"
                value={restrictedAgeYears}
                onChange={(event) => setRestrictedAgeYears(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.ageHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mk-tax-groups">
                {t('settings.integrations.masterkasa.form.taxGroups')}
              </Label>
              <Input
                id="mk-tax-groups"
                value={taxGroups}
                placeholder="5"
                onChange={(event) => setTaxGroups(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.taxGroupsHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mk-shop-id">
                {t('settings.integrations.masterkasa.form.shopId')}
              </Label>
              <Input
                id="mk-shop-id"
                value={shopId}
                maxLength={32}
                placeholder="2101"
                onChange={(event) => setShopId(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.shopIdHint')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mk-age-asset">
                {t('settings.integrations.masterkasa.form.ageAssetKey')}
              </Label>
              <Input
                id="mk-age-asset"
                value={ageAssetKey}
                maxLength={64}
                onChange={(event) => setAgeAssetKey(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.ageAssetKeyHint')}
              </p>
            </div>
          </div>

          {!creating && (
            <fieldset className="space-y-3 border-t border-border pt-4">
              <legend className="text-sm font-semibold">
                {t('settings.integrations.masterkasa.form.exportTitle')}
              </legend>
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.exportDescription')}
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <input
                    id="mk-export-enabled"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={exportEnabled}
                    onChange={(event) => setExportEnabled(event.target.checked)}
                  />
                  <Label htmlFor="mk-export-enabled">
                    {t('settings.integrations.masterkasa.form.exportEnabled')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="mk-export-z"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={exportZReports}
                    onChange={(event) => setExportZReports(event.target.checked)}
                  />
                  <Label htmlFor="mk-export-z">
                    {t('settings.integrations.masterkasa.form.exportZReports')}
                  </Label>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="mk-export-username">
                    {t('settings.integrations.masterkasa.form.exportUsername')}
                  </Label>
                  <Input
                    id="mk-export-username"
                    value={exportUsername}
                    maxLength={128}
                    autoComplete="off"
                    onChange={(event) => setExportUsername(event.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mk-export-password">
                    {t('settings.integrations.masterkasa.form.exportPassword')}
                  </Label>
                  <Input
                    id="mk-export-password"
                    type="password"
                    autoComplete="new-password"
                    value={exportPassword}
                    placeholder={
                      source?.exportPasswordSet === true
                        ? t('settings.integrations.masterkasa.form.exportPasswordKeep')
                        : ''
                    }
                    onChange={(event) => setExportPassword(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {source?.exportPasswordSet === true
                      ? t('settings.integrations.masterkasa.form.exportPasswordSet')
                      : t('settings.integrations.masterkasa.form.exportPasswordUnset')}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mk-cash-in">
                    {t('settings.integrations.masterkasa.form.cashInReceiptType')}
                  </Label>
                  <Input
                    id="mk-cash-in"
                    inputMode="numeric"
                    value={cashIn}
                    onChange={(event) => setCashIn(event.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mk-cash-out">
                    {t('settings.integrations.masterkasa.form.cashOutReceiptType')}
                  </Label>
                  <Input
                    id="mk-cash-out"
                    inputMode="numeric"
                    value={cashOut}
                    onChange={(event) => setCashOut(event.target.value)}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.masterkasa.form.exportShopIdHint')}
              </p>
            </fieldset>
          )}

          {creating && (
            <p className="text-xs text-muted-foreground">
              {t('settings.integrations.masterkasa.form.firstImportWarning')}
            </p>
          )}
          {error !== null && <p className="text-sm text-destructive">{error}</p>}

          <AlertDialogFooter>
            <AlertDialogCancel type="button" onClick={onClose}>
              {t('settings.integrations.cancel')}
            </AlertDialogCancel>
            <Button type="submit" disabled={pending || !canSubmit}>
              {creating
                ? t('settings.integrations.masterkasa.form.createSubmit')
                : t('settings.integrations.masterkasa.form.settingsSubmit')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
